<script setup lang="ts">
import {
	CART_REMOVE_DEFAULT,
	CART_STEPPER_DEFAULT,
	type CartBoxIconControl,
	type CartStepperControl,
	cartRemoveVariants,
	cartStepperVariants,
} from "@innoclouds/ui/templates/cart/cartBoxControls/cartBoxControls.ts";
import { useTranslation } from "i18next-vue";
import { computed, onMounted, ref, watch } from "vue";
import crossIcon from "#/assets/icons/cross.svg";
import minusIcon from "#/assets/icons/minus.svg";
import plusIcon from "#/assets/icons/plus.svg";
import IconLocked from "#/assets/iconsDynamic/Locked.vue";
import IconMark from "#/assets/iconsDynamic/Mark.vue";
import PlusOrange from "#/assets/iconsDynamic/PlusOrange.vue";
import CartItemComment from "#/components/cartItemComment/CartItemComment.vue";
import { useAnimationNum } from "#/hooks/useAnimationNum/useAnimationNum";
import { useCartStore } from "#/store/cart";
import { useDecorationStore } from "#/store/decoration";
import { useMainStore } from "#/store/main";
import { useModalStore } from "#/store/modals";
import { useThemeStore } from "#/store/theme";
import { type FreeModifierBreakdown, getCartItemModifierBreakdowns, getCartItemModifiersPaidTotal } from "#/utils/freeModifiers";
import { formatPrice, getLocaleAltName, getLocaleName } from "#/utils/i18next";
import PricePrerender from "#/utils/pricePrerender";

interface IProps {
	index?: number;
	cardItem: any;
	isQr: boolean;
	isKiosk: boolean;
	hasPromoCode: boolean;
	decoration?: string;
}

const props = defineProps<IProps>();

const decor = useDecorationStore().screen(props.decoration);

const storeMain = useMainStore();
const themeStore = useThemeStore();
const cartStore = useCartStore();
const modals = useModalStore();
const { t, i18next } = useTranslation();

const startX = ref(0);
const currentX = ref(0);
const isDragging = ref(false);
const isAnimating = ref(false);
const showModsByDefault = props.isQr ? (themeStore.data?.qr_show_modifiers_in_cart ?? false) : (themeStore.data?.kiosk_show_modifiers_in_cart ?? false);

const isExpanded = ref(showModsByDefault);

const { displayNumber: displayPrice, update: updatePrice } = useAnimationNum(0, 800);

const arrAllModifiers = computed(() => {
	const mods = props.cardItem?.modifiers ?? [];
	const groupMods = (props.cardItem?.group_modifiers ?? []).flatMap((group: any) => group.modifiers ?? []);
	return [...mods, ...groupMods];
});

const sizeName = computed(() =>
	getLocaleAltName(
		props.cardItem?.sizes?.find((size: any) => size.id === props.cardItem?.size),
		i18next.language,
	),
);

const stepperContent = computed(
	() => cartStepperVariants[decor.variant("line_stepper_button", CART_STEPPER_DEFAULT)] ?? cartStepperVariants[CART_STEPPER_DEFAULT],
);
const minusControl = computed((): CartStepperControl => ({ label: "-", icon: decor.icon("line_stepper_minus"), fallback: minusIcon }));
const plusControl = computed((): CartStepperControl => ({ label: "+", icon: decor.icon("line_stepper_plus"), fallback: plusIcon }));
const removeContent = computed(() => cartRemoveVariants[decor.variant("line_remove", CART_REMOVE_DEFAULT)] ?? cartRemoveVariants[CART_REMOVE_DEFAULT]);
const removeControl = computed((): CartBoxIconControl => ({ icon: decor.icon("line_remove"), fallback: crossIcon }));

const lineOptions = computed(() => {
	if (!decor.visible("line_options", false)) return [];
	const symbol = storeMain.config_lib.currency_symbol;
	const modifiers = arrAllModifiers.value.map((modifier) => {
		const paid = getModifierPaidTotal(modifier);
		return {
			id: modifier.id,
			name: getLocaleName(modifier, i18next.language),
			amount: modifier.amount > 1 ? `×${modifier.amount}` : "",
			price: paid > 0 ? `+${formatPrice(paid, symbol)}` : "",
			size: false,
		};
	});
	return sizeName.value ? [{ id: "size", name: sizeName.value, amount: "", price: "", size: true }, ...modifiers] : modifiers;
});

const isCombo = computed(() => !!(props.cardItem as any)?.is_combo);
const isVisibleComment = computed(
	() => !isCombo.value && ((props.isQr && themeStore.data?.comments?.qr_item_comment) || (props.isKiosk && themeStore.data?.comments?.kiosk_item_comment)),
);
const commentHints = computed(() => cartStore.orderItemCommentSuggestions || []);

const comboGroups = computed(() => {
	if (!isCombo.value) return [];
	const combos = (props.cardItem as any)?.combos || [];
	const firstCombo = combos[0] || {};
	const groups = firstCombo.groups || [];
	return groups.map((group: any) => ({
		id: group.id,
		name: group.name,
		loc_name: group.loc_name,
		item: group.item || {},
	}));
});

const expandedDishes = ref<Set<string>>(new Set(showModsByDefault ? comboGroups.value.map((g: any) => g.id) : []));
const isDishExpanded = (id: string) => expandedDishes.value.has(id);
const toggleDishMods = (id: string) => {
	const next = new Set(expandedDishes.value);
	next.has(id) ? next.delete(id) : next.add(id);
	expandedDishes.value = next;
};

const getComboSurcharge = (item: any) => Number(item?.price_modification ?? 0) || 0;
const getComboItemSize = (item: any) => item?.size_name || item?.size?.name || item?.size?.alt_name || "";

const getComboItemModifiers = (item: any) => {
	const mods = item?.modifiers ?? [];
	const groupMods = (item?.group_modifiers ?? []).flatMap((g: any) => g.modifiers ?? []);
	return [...mods, ...groupMods];
};

const comboModifiers = computed(() =>
	comboGroups.value.map((group: any) => ({
		...group,
		modifiers: getComboItemModifiers(group.item),
		modifierBreakdowns: getCartItemModifierBreakdowns(group.item),
	})),
);

const totalPrice = computed(() => props.cardItem?.total_new_price ?? PricePrerender(props.cardItem) * props.cardItem?.amount);

const totalModifierPrice = computed(() => {
	if (isCombo.value) return 0;
	const deltas = arrAllModifiers.value.reduce((acc, modifier) => acc + (+modifier.delta_price || 0), 0);
	return getCartItemModifiersPaidTotal(props.cardItem) * (props.cardItem?.amount ?? 1) + deltas;
});

const discountsList = computed(() => {
	const discounts = [...(props.cardItem?.discounts || []), ...arrAllModifiers.value.flatMap((modifier) => modifier.discounts || [])];
	return Array.from(new Set(discounts.flatMap((d) => d.name)));
});

const lineTotal = computed(() => Number(totalPrice.value) + Number(totalModifierPrice.value));
const originalTotal = computed(() => {
	const amount = props.cardItem?.amount ?? 1;
	const modifiersTotal = isCombo.value ? 0 : getCartItemModifiersPaidTotal(props.cardItem, "old_price") * amount;
	return Number(props.cardItem?.old_price ?? 0) * amount + modifiersTotal;
});
const isOldPriceVisible = computed(
	() => decor.visible("line_old_price", false) && discountsList.value.length > 0 && originalTotal.value - lineTotal.value >= 0.01,
);

const modifierBreakdowns = computed(() => getCartItemModifierBreakdowns(props.cardItem));

const getBreakdown = (modifier: any, breakdowns?: Map<string, FreeModifierBreakdown>) => (breakdowns ?? modifierBreakdowns.value).get(modifier.id);

const getModifierPaidTotal = (modifier: any, breakdowns?: Map<string, FreeModifierBreakdown>) => {
	if (modifier.total_new_price != null) return Number(modifier.total_new_price);
	return getBreakdown(modifier, breakdowns)?.paidTotal ?? modifier.price * modifier.amount;
};

const getFreeAmount = (modifier: any, breakdowns?: Map<string, FreeModifierBreakdown>) => getBreakdown(modifier, breakdowns)?.freeAmount ?? 0;

const getPaidAmount = (modifier: any, breakdowns?: Map<string, FreeModifierBreakdown>) =>
	getBreakdown(modifier, breakdowns)?.paidAmount ?? modifier.amount ?? 0;

const shouldShowModifierFormula = (modifier: any, breakdowns?: Map<string, FreeModifierBreakdown>) =>
	modifier.amount > 1 || (props.cardItem?.amount ?? 0) > 1 || getFreeAmount(modifier, breakdowns) > 0;

const showRemoveModal = () => {
	if (storeMain.cartRemoveInterceptor?.(props.cardItem)) return;
	modals.open("cart-delete-confirm", props.cardItem);
};

const toggleExpand = () => {
	isExpanded.value = !isExpanded.value;
};

const increment = () => {
	if (!props.cardItem || props.cardItem?.is_marked) return;
	if (props.hasPromoCode) return modals.open("cart-promo-reset-confirm", { callback: () => storeMain.plusAmount(props.cardItem.idSystem) });

	return storeMain.plusAmount(props.cardItem.idSystem);
};

const decrement = () => {
	if (!props.cardItem) return;
	if (props.cardItem.amount > 1) {
		if (props.hasPromoCode) return modals.open("cart-promo-reset-confirm", { callback: () => storeMain.minusAmount(props.cardItem.idSystem) });

		return storeMain.minusAmount(props.cardItem.idSystem);
	}

	return showRemoveModal();
};

const handleTouchStart = (e: TouchEvent) => {
	startX.value = e.touches[0].clientX;
	isDragging.value = true;
};

const handleSubmitComment = (comment: string) => {
	props.cardItem.comment = comment;
	storeMain.updateCartInLocalStorage();
};

const handleClearComment = () => {
	props.cardItem.comment = null;
	storeMain.updateCartInLocalStorage();
};

const handleTouchMove = (e: TouchEvent) => {
	if (!isDragging.value) return;
	currentX.value = e.touches[0].clientX - startX.value;
	if (currentX.value > 0) currentX.value = 0;
};

const handleTouchEnd = () => {
	isDragging.value = false;
	if (currentX.value < -115) showRemoveModal();
	currentX.value = 0;
};

onMounted(() => {
	if (props.index === 0) {
		isAnimating.value = true;
		setTimeout(() => {
			currentX.value = -90;
			setTimeout(() => {
				currentX.value = 0;
				isAnimating.value = false;
			}, 700);
		}, 0);
	}
});

const boxStyle = computed(() => ({
	transform: `translateX(${currentX.value}px)`,
	opacity: props.index === 0 && isAnimating.value ? 1 : `${1 - Math.abs(currentX.value) / 200}`,
	transition: isDragging.value ? "none" : "transform 0.7s, opacity 0.7s",
}));

watch([totalPrice, totalModifierPrice], ([newTotal, newTotalModifierPrice]) => updatePrice(newTotal + newTotalModifierPrice), { immediate: true });
</script>

<template>
	<div
		class="cart_box_wrapper"
		@touchstart="handleTouchStart"
		@touchmove="handleTouchMove"
		@touchend="handleTouchEnd"
		:style="boxStyle"
		v-bind="decor.bind('line')"
	>
		<div class="wrapper_cart_delete">
			<div class="cart_delete"></div>
		</div>

		<div class="image_wrapper" :class="{ 'adult': cardItem?.adult && !storeMain.is18Allowed }" v-bind="decor.bind('line_photo')">
			<img
				:src="cardItem?.image_link || themeStore.data?.fallback_image || ''"
				alt=""
				class="cart_box_image"
				loading="lazy"
				crossorigin="anonymous"
				v-bind="decor.bind('line_image')"
			>
		</div>

		<div class="cart_box_content_wrapper" v-bind="decor.bind('line_content')">
			<div class="cart_box_content_info" v-bind="decor.bind('line_info')">
				<p class="cart_box_name" v-bind="decor.bind('line_name')">{{ getLocaleName(cardItem, i18next.language) }}</p>
				<p class="cart_box_size" v-if="sizeName && decor.visible('line_size')" v-bind="decor.bind('line_size')">
					{{ sizeName }}
				</p>
				<div v-if="lineOptions.length" class="cart_box_options" v-bind="decor.bind('line_options')">
					<span v-for="option in lineOptions" :key="option.id" class="cart_box_option" v-bind="decor.bind('line_option', { size: option.size })">
						{{ option.name }}
						<template v-if="option.amount">{{ option.amount }}</template>
						<b v-if="option.price" class="cart_box_option_price" v-bind="decor.bind('line_option_price')">{{ option.price }}</b>
					</span>
				</div>
				<div class="discounts_list" v-bind="decor.bind('line_badges')">
					<div
						v-if="isKiosk && themeStore.data?.kiosk_age_confirmation_enabled && cardItem?.adult"
						class="discount_banner adult"
						:class="{ confirmed: cartStore.isAdultConfirmed }"
					>
						<IconMark v-if="cartStore.isAdultConfirmed" />
						<IconLocked v-else />
						<p>18+</p>
					</div>
					<div v-for="discount in discountsList" :key="discount" class="discount_banner" v-bind="decor.bind('line_badge')">
						<p v-bind="decor.bind('line_badge_text')">{{ discount }}</p>
					</div>
				</div>
				<div class="cart_amount" v-bind="decor.bind('line_stepper')">
					<button type="button" @click="decrement" v-bind="decor.bind('line_stepper_button')">
						<component :is="stepperContent" :control="minusControl" />
					</button>
					<p class="cart_amount_count" :data-disabled="cardItem?.is_marked" v-bind="decor.bind('line_stepper_count')">{{ cardItem?.amount || 1 }}</p>
					<button type="button" @click="increment" :disabled="cardItem?.is_marked" v-bind="decor.bind('line_stepper_button')">
						<component :is="stepperContent" :control="plusControl" />
					</button>
				</div>
			</div>

			<div class="cart_box_info_more" v-bind="decor.bind('line_more')">
				<div class="cart_box_remove_item" @click="showRemoveModal" v-bind="decor.bind('line_remove')">
					<component :is="removeContent" :control="removeControl" />
				</div>
				<p v-bind="decor.bind('line_price', { discount: isOldPriceVisible })">
					<template v-if="isOldPriceVisible">
						<s class="cart_box_old_price" v-bind="decor.bind('line_old_price')">{{ formatPrice(originalTotal, storeMain.config_lib.currency_symbol) }}</s>
						<span class="cart_box_new_price" v-bind="decor.bind('line_new_price')">
							{{ formatPrice(displayPrice, storeMain.config_lib.currency_symbol) }}
						</span>
					</template>
					<template v-else>{{ formatPrice(displayPrice, storeMain.config_lib.currency_symbol) }}</template>
				</p>
			</div>
		</div>

		<div v-if="isCombo && comboGroups.length > 0" class="cart_combo_composition">
			<div class="cart_combo_composition_header" @click.stop="toggleExpand">
				<p class="cart_combo_composition_title">{{ t(`cart-box.composition`) }} · {{ t(`cart-box.dishes`, { count: comboGroups.length }) }}</p>
				<span class="cart_combo_chevron" :class="{ cart_combo_chevron_open: isExpanded }"></span>
			</div>

			<div v-if="isExpanded" class="cart_combo_dishes">
				<div class="cart_modifier_title">
					{{ formatPrice(cardItem?.price, storeMain.config_lib.currency_symbol) }}
					× {{ cardItem?.amount }} =
					{{ formatPrice(totalPrice, storeMain.config_lib.currency_symbol) }}
				</div>
				<div v-for="group in comboModifiers" :key="group.id" class="cart_combo_dish">
					<div class="cart_combo_dish_head">
						<div class="cart_combo_dish_name">
							<span class="cart_combo_bullet"></span>
							<p>{{ group.item?.name || group.name }}</p>
						</div>
						<p v-if="getComboSurcharge(group.item) > 0" class="cart_combo_surcharge">
							+{{ formatPrice(getComboSurcharge(group.item) * (cardItem?.amount ?? 1), storeMain.config_lib.currency_symbol) }}
						</p>
						<p v-else class="cart_combo_included">{{ t(`cart-box.included`) }}</p>
					</div>

					<p v-if="getComboSurcharge(group.item) > 0 && cardItem?.amount > 1" class="cart_combo_dish_calc">
						{{ formatPrice(getComboSurcharge(group.item), storeMain.config_lib.currency_symbol) }}
						× {{ cardItem?.amount }}
					</p>

					<p v-if="getComboItemSize(group.item)" class="cart_combo_dish_size">{{ getComboItemSize(group.item) }}</p>

					<template v-if="group.modifiers.length > 0">
						<div class="cart_combo_dish_mods_toggle" @click.stop="toggleDishMods(group.id)">
							<span class="cart_combo_chevron" :class="{ cart_combo_chevron_open: isDishExpanded(group.id) }"></span>
							<p>{{ isDishExpanded(group.id) ? t(`cart-box.hide-modifiers`) : t(`cart-box.show-modifiers`) }}</p>
						</div>
						<div v-if="isDishExpanded(group.id)" class="cart_combo_dish_mods">
							<div
								v-for="modifier in group.modifiers"
								:key="modifier.id"
								class="cart_combo_mod_item"
								:class="{ cart_modifier_item_free: getModifierPaidTotal(modifier, group.modifierBreakdowns) === 0 }"
							>
								<div class="cart_modifier_content">
									<p>{{ getLocaleName(modifier, i18next.language) }}</p>
									<div v-if="shouldShowModifierFormula(modifier, group.modifierBreakdowns)" class="count_cart_box cart_modifier_calculation">
										<div v-if="getPaidAmount(modifier, group.modifierBreakdowns) > 0" class="cart_modifier_calculation_line">
											{{ formatPrice(modifier.price, storeMain.config_lib.currency_symbol) }}
											× {{ getPaidAmount(modifier, group.modifierBreakdowns) }} =
											{{ formatPrice(getModifierPaidTotal(modifier, group.modifierBreakdowns), storeMain.config_lib.currency_symbol) }}
											<template v-if="cardItem?.amount > 1"> × {{ cardItem?.amount }}</template>
										</div>
										<div v-if="getFreeAmount(modifier, group.modifierBreakdowns) > 0" class="cart_modifier_calculation_line cart_modifier_calculation_free">
											{{ t(`cart-box.free-count`, { count: getFreeAmount(modifier, group.modifierBreakdowns) }) }}
											<template v-if="cardItem?.amount > 1"> × {{ cardItem?.amount }}</template>
										</div>
									</div>
								</div>
								<div class="cart_combo_mod_price">
									<PlusOrange id="plus_orange" />
									<div class="wrapper_modifiers_discount">
										<p v-if="modifier?.new_price" class="discount_new_price">
											{{ formatPrice(modifier.new_price * modifier.amount * (cardItem?.amount ?? 1), storeMain.config_lib.currency_symbol) }}
										</p>
										<p>
											{{ formatPrice(getModifierPaidTotal(modifier, group.modifierBreakdowns) * (cardItem?.amount ?? 1), storeMain.config_lib.currency_symbol) }}
										</p>
									</div>
								</div>
							</div>
						</div>
					</template>
				</div>
			</div>
		</div>

		<div
			class="cart_modifier_toggle"
			v-if="!isCombo && !isExpanded && arrAllModifiers.length > 0 && decor.visible('line_modifiers_toggle')"
			@click.stop="toggleExpand"
			v-bind="decor.bind('line_modifiers_toggle')"
		>
			<p class="cart_info_more">{{ isCombo ? t(`cart-box.show-combo`) : t(`cart-box.show-modifiers`) }}</p>
			<p class="cart_arrow"></p>
		</div>

		<div v-if="!isCombo && isExpanded && arrAllModifiers.length > 0" class="cart_modifier_info">
			<div class="cart_modifier_title">
				{{ formatPrice(cardItem?.price, storeMain.config_lib.currency_symbol) }}
				× {{ cardItem?.amount }} =
				{{ formatPrice(totalPrice, storeMain.config_lib.currency_symbol) }}
			</div>
			<div
				v-for="modifier in arrAllModifiers"
				:key="modifier.id"
				class="cart_modifier_item"
				:class="{ cart_modifier_item_free: getModifierPaidTotal(modifier) === 0 }"
			>
				<div class="cart_modifier_content">
					<div class="cart_modifier_text">
						<p class="cart_modifier_name">{{ getLocaleName(modifier, i18next.language) }}</p>
						<div v-if="shouldShowModifierFormula(modifier)" class="cart_modifier_calculation">
							<div v-if="getPaidAmount(modifier) > 0" class="cart_modifier_calculation_line">
								{{ formatPrice(modifier.price, storeMain.config_lib.currency_symbol) }}
								× {{ getPaidAmount(modifier) }} = {{ formatPrice(getModifierPaidTotal(modifier), storeMain.config_lib.currency_symbol) }}
								<template v-if="cardItem?.amount > 1"> × {{ cardItem?.amount }}</template>
							</div>
							<div v-if="getFreeAmount(modifier) > 0" class="cart_modifier_calculation_line cart_modifier_calculation_free">
								{{ t(`cart-box.free-count`, { count: getFreeAmount(modifier) }) }}
								<template v-if="cardItem?.amount > 1"> × {{ cardItem?.amount }}</template>
							</div>
						</div>
					</div>
				</div>

				<div class="wrapper_price_count_cart">
					{{ formatPrice(getModifierPaidTotal(modifier) * (cardItem?.amount ?? 1) + (+modifier.delta_price || 0), storeMain.config_lib.currency_symbol ) }}
				</div>
			</div>

			<div class="cart_modifier_arrow" @click.stop="toggleExpand">
				<p class="cart_info_more">
					{{ isCombo ? t(`cart-box.hide-combo`) : t(`cart-box.hide-modifiers`) }}
				</p>
				<p class="cart_arrow_top"></p>
			</div>
		</div>

		<CartItemComment
			v-if="isVisibleComment"
			editable
			:value="cardItem?.comment"
			:hints="commentHints"
			@submit="handleSubmitComment"
			@clear="handleClearComment"
		/>
	</div>
</template>

<style scoped lang="scss">
  @use './cartBox.scss';
</style>
